import {isFulfilled} from '@reduxjs/toolkit';

import {accountSettingsSelector} from 'app/settings/api';
import {setReloadRequired} from 'app/settings/slice';
import {withSentryScope} from 'app/utils/sentry';

import type {AppDispatchType} from '../AppStore';
import type {AnyAction, Middleware} from '@reduxjs/toolkit';

// flag used to show the message once
let sentToSentry = false;
const STORAGE_KEY = 'seen-html-versions';
const MAX_BUFFER = 3;

function getStoredVersions(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function addVersionToStorage(version: string) {
  const current = Array.from(getStoredVersions());
  if (!current.includes(version)) {
    const updated = [...current, version].slice(-MAX_BUFFER);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }
}

export const AppVersionCheckerMiddleware: Middleware =
  (store) => (next: AppDispatchType) => (action: AnyAction) => {
    if (!isFulfilled(action)) {
      return next(action);
    }

    const state = store.getState();
    const appHTMLVersion =
      accountSettingsSelector(state).data?.APP_HTML_VERSION;
    if (!appHTMLVersion) {
      return next(action);
    }

    const meta = action.meta as any;
    const latestHTMLVersion: string | null =
      meta?.baseQueryMeta?.response?.headers?.get?.('X-HTML-Version');
    if (!latestHTMLVersion) {
      return next(action);
    }

    const seenVersions = getStoredVersions();

    // Always attempt to persist our core settings version.
    addVersionToStorage(appHTMLVersion);

    const isNewVersion = !seenVersions.has(latestHTMLVersion);
    const differsFromFrontend = latestHTMLVersion !== appHTMLVersion;

    if (isNewVersion && differsFromFrontend) {
      store.dispatch(setReloadRequired(true));
      addVersionToStorage(latestHTMLVersion); // also store the new version. Customer is about to reload.
      if (!sentToSentry) {
        sentToSentry = true;
        withSentryScope((scope) => {
          scope.setLevel('info');
          scope.setExtra('frontVersion', appHTMLVersion);
          scope.setExtra('backVersion', latestHTMLVersion);
          scope.setExtra('triggerURL', meta?.baseQueryMeta?.request?.url);
        });
      }
    }

    return next(action);
  };
